/**
 * Selection Preview Sync Hook
 *
 * Automatically re-opens the preview panel when transitioning from multi-select
 * back to single selection. This provides a seamless UX where deselecting items
 * during multi-select will restore the preview for the remaining item.
 *
 * Use Case:
 * 1. User selects item A -> preview opens for A
 * 2. User Cmd+clicks item B -> multi-select, preview closes
 * 3. User Cmd+clicks item B again to deselect -> single selection restored
 * 4. This hook detects the transition and re-opens preview for item A
 *
 * @module features/workspace/hooks/useSelectionPreviewSync
 */

import { useRef, useEffect, useCallback } from 'react';
import type { WorkspaceItemType } from '@loader/shared-types';

import { useSelectionStore } from '../stores/useSelectionStore';

/**
 * Item with minimum required properties for selection preview sync
 */
interface SelectableItem {
  id: string;
  type: WorkspaceItemType;
}

/**
 * Options for useSelectionPreviewSync hook
 */
export interface UseSelectionPreviewSyncOptions {
  /** List of workspace items that can be selected */
  items: SelectableItem[];
  /** Callback to open preview for an item (not called for folders) */
  onPreview?: (id: string, type: WorkspaceItemType) => void;
}

/**
 * Synchronizes selection state with preview panel.
 *
 * Monitors selection changes and automatically triggers preview when
 * transitioning from multi-select (>1 item) back to single selection (1 item).
 * Folders are excluded from preview as they don't have a preview panel.
 *
 * @param options - Configuration options
 * @param options.items - List of selectable items
 * @param options.onPreview - Callback to open preview panel
 *
 * @example
 * ```tsx
 * function FolderView({ items, onPreview }) {
 *   useSelectionPreviewSync({ items, onPreview });
 *
 *   return (
 *     <div>
 *       {items.map(item => <ItemCard key={item.id} {...item} />)}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSelectionPreviewSync({
  items,
  onPreview,
}: UseSelectionPreviewSyncOptions): void {
  // Subscribe to selectedIds from the selection store
  const selectedIds = useSelectionStore((state) => state.selectedIds);

  // Track previous selection size to detect multi->single transitions
  const prevSelectionSizeRef = useRef(selectedIds.size);

  // Find item by ID helper
  const findItemById = useCallback(
    (id: string) => items.find((item) => item.id === id),
    [items]
  );

  // Re-open preview when going from multi-select back to single selection
  useEffect(() => {
    const prevSize = prevSelectionSizeRef.current;
    const currentSize = selectedIds.size;

    // Update ref for next render
    prevSelectionSizeRef.current = currentSize;

    // If we went from multi (>1) to single (=1), open preview for the remaining item
    if (prevSize > 1 && currentSize === 1) {
      const selectedId = Array.from(selectedIds)[0];
      const item = findItemById(selectedId);
      if (item && item.type !== 'folder') {
        onPreview?.(item.id, item.type);
      }
    }
  }, [selectedIds, findItemById, onPreview]);
}
